export type AggregationType = 'none' | 'count' | 'sum' | 'avg' | 'min' | 'max'
export type ChartType = 'bar' | 'line' | 'area' | 'pie' | 'table'
export type DateGrouping = 'day' | 'week' | 'month' | 'quarter' | 'year'

export interface ReportField {
  id: string
  name: string
  label: string
  type: 'string' | 'number' | 'date' | 'boolean'
  selected: boolean
  aggregation: AggregationType
}

export interface ReportFilter {
  id: string
  field: string
  operator: string
  value: string
}

export interface ReportConfig {
  name: string
  dataSource: string
  fields: ReportField[]
  filters: ReportFilter[]
  groupBy?: string
  dateGrouping?: DateGrouping
  sortBy?: string
  sortOrder: 'asc' | 'desc'
  chartType: ChartType
  limit: number
}

export const DATA_SOURCES = [
  {
    value: 'opportunities',
    label: 'Opportunities',
    description: 'Deals, revenue, pipeline stages, and close dates',
  },
  {
    value: 'accounts',
    label: 'Accounts',
    description: 'Companies, industries, revenue, and employee counts',
  },
  {
    value: 'contacts',
    label: 'Contacts',
    description: 'People, email addresses, titles, and account links',
  },
  {
    value: 'leads',
    label: 'Leads',
    description: 'Prospects, sources, ratings, and conversion status',
  },
  {
    value: 'tasks',
    label: 'Tasks',
    description: 'Activities, priorities, due dates, and assignments',
  },
  {
    value: 'call_logs',
    label: 'Call Logs',
    description: 'Phone calls, durations, dispositions, and outcomes',
  },
]

export const FEATURED_TEMPLATE_IDS = [
  'pipeline-by-stage',
  'revenue-trend',
  'leads-by-source',
  'tasks-by-status',
  'activities-created-this-month',
  'top-accounts-revenue',
]

export const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'greater_or_equal', label: 'Greater or Equal' },
  { value: 'less_or_equal', label: 'Less or Equal' },
  { value: 'is_null', label: 'Is Empty' },
  { value: 'is_not_null', label: 'Is Not Empty' },
]

export const AGGREGATIONS: { value: AggregationType; label: string; numericOnly: boolean }[] = [
  { value: 'none', label: 'No Aggregation', numericOnly: false },
  { value: 'count', label: 'Count', numericOnly: false },
  { value: 'sum', label: 'Sum', numericOnly: true },
  { value: 'avg', label: 'Average', numericOnly: true },
  { value: 'min', label: 'Minimum', numericOnly: true },
  { value: 'max', label: 'Maximum', numericOnly: true },
]

export const POPULAR_FIELDS: Record<string, string[]> = {
  opportunities: ['name', 'amount', 'stage_name', 'close_date', 'probability'],
  accounts: ['name', 'industry', 'annual_revenue', 'type', 'rating'],
  contacts: ['first_name', 'last_name', 'email', 'title', 'account_name'],
  leads: ['name', 'company', 'status', 'lead_source', 'rating'],
  tasks: ['subject', 'status', 'priority', 'activity_date', 'owner_name'],
}

export interface QuickFilter {
  id: string
  label: string
  icon: 'zap' | 'star' | 'calendar'
  filters: { field: string; operator: string; value: string }[]
}

export const QUICK_FILTERS: Record<string, QuickFilter[]> = {
  opportunities: [
    {
      id: 'open-only',
      label: 'Open Only',
      icon: 'zap',
      filters: [{ field: 'is_closed', operator: 'equals', value: 'false' }],
    },
    {
      id: 'high-value',
      label: 'High Value ($50K+)',
      icon: 'star',
      filters: [{ field: 'amount', operator: 'greater_or_equal', value: '50000' }],
    },
    {
      id: 'closing-soon',
      label: 'Closing This Month',
      icon: 'calendar',
      filters: [],
    },
  ],
  accounts: [
    {
      id: 'customers',
      label: 'Customers Only',
      icon: 'zap',
      filters: [{ field: 'type', operator: 'equals', value: 'Customer' }],
    },
    {
      id: 'high-revenue',
      label: 'High Revenue ($1M+)',
      icon: 'star',
      filters: [{ field: 'annual_revenue', operator: 'greater_or_equal', value: '1000000' }],
    },
  ],
  contacts: [
    {
      id: 'has-email',
      label: 'Has Email',
      icon: 'zap',
      filters: [{ field: 'email', operator: 'is_not_null', value: '' }],
    },
  ],
  leads: [
    {
      id: 'new-leads',
      label: 'New Leads',
      icon: 'zap',
      filters: [{ field: 'status', operator: 'equals', value: 'New' }],
    },
    {
      id: 'hot-leads',
      label: 'Hot Leads',
      icon: 'star',
      filters: [{ field: 'rating', operator: 'equals', value: 'Hot' }],
    },
    {
      id: 'not-converted',
      label: 'Not Converted',
      icon: 'calendar',
      filters: [{ field: 'is_converted', operator: 'equals', value: 'false' }],
    },
  ],
  tasks: [
    {
      id: 'open-tasks',
      label: 'Open Tasks',
      icon: 'zap',
      filters: [{ field: 'is_closed', operator: 'equals', value: 'false' }],
    },
    {
      id: 'high-priority',
      label: 'High Priority',
      icon: 'star',
      filters: [{ field: 'priority', operator: 'equals', value: 'High' }],
    },
  ],
}

export interface DateRangePreset {
  label: string
  value: string
  getRange: () => { start: string; end: string }
}

const formatDate = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export { formatDate }

export const DATE_RANGE_PRESETS: DateRangePreset[] = [
  {
    label: 'Today',
    value: 'today',
    getRange: () => {
      const today = formatDate(new Date())
      return { start: today, end: today }
    },
  },
  {
    label: 'This Week',
    value: 'this_week',
    getRange: () => {
      const now = new Date()
      const start = new Date(now)
      start.setDate(now.getDate() - now.getDay())
      const end = new Date(start)
      end.setDate(start.getDate() + 6)
      return { start: formatDate(start), end: formatDate(end) }
    },
  },
  {
    label: 'This Month',
    value: 'this_month',
    getRange: () => {
      const now = new Date()
      const start = new Date(now.getFullYear(), now.getMonth(), 1)
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      return { start: formatDate(start), end: formatDate(end) }
    },
  },
  {
    label: 'Last Month',
    value: 'last_month',
    getRange: () => {
      const now = new Date()
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const end = new Date(now.getFullYear(), now.getMonth(), 0)
      return { start: formatDate(start), end: formatDate(end) }
    },
  },
  {
    label: 'This Quarter',
    value: 'this_quarter',
    getRange: () => {
      const now = new Date()
      const quarter = Math.floor(now.getMonth() / 3)
      const start = new Date(now.getFullYear(), quarter * 3, 1)
      const end = new Date(now.getFullYear(), quarter * 3 + 3, 0)
      return { start: formatDate(start), end: formatDate(end) }
    },
  },
  {
    label: 'Last Quarter',
    value: 'last_quarter',
    getRange: () => {
      const now = new Date()
      const quarter = Math.floor(now.getMonth() / 3) - 1
      const year = quarter < 0 ? now.getFullYear() - 1 : now.getFullYear()
      const adjustedQuarter = quarter < 0 ? 3 : quarter
      const start = new Date(year, adjustedQuarter * 3, 1)
      const end = new Date(year, adjustedQuarter * 3 + 3, 0)
      return { start: formatDate(start), end: formatDate(end) }
    },
  },
  {
    label: 'Year to Date',
    value: 'ytd',
    getRange: () => {
      const now = new Date()
      const start = new Date(now.getFullYear(), 0, 1)
      return { start: formatDate(start), end: formatDate(now) }
    },
  },
  {
    label: 'Last Year',
    value: 'last_year',
    getRange: () => {
      const now = new Date()
      const start = new Date(now.getFullYear() - 1, 0, 1)
      const end = new Date(now.getFullYear() - 1, 11, 31)
      return { start: formatDate(start), end: formatDate(end) }
    },
  },
]

export const COMPARABLE_PERIODS = ['this_month', 'this_quarter', 'ytd']

export const DATE_GROUPINGS: { value: DateGrouping; label: string }[] = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
]

export interface ReportTemplate {
  id: string
  name: string
  description: string
  category: 'sales' | 'leads' | 'activities' | 'support' | 'accounts'
  dataSource: string
  fields: { name: string; aggregation: AggregationType }[]
  groupBy?: string
  dateGrouping?: DateGrouping
  chartType: ChartType
  filters?: { field: string; operator: string; value: string }[]
  dateField?: string
}

export const REPORT_TEMPLATES: ReportTemplate[] = [
  // Sales (6)
  {
    id: 'pipeline-by-stage',
    name: 'Pipeline by Stage',
    description: 'Count and total value of opportunities by sales stage',
    category: 'sales',
    dataSource: 'opportunities',
    fields: [
      { name: 'stage_name', aggregation: 'none' },
      { name: 'amount', aggregation: 'sum' },
      { name: 'name', aggregation: 'count' },
    ],
    groupBy: 'stage_name',
    chartType: 'bar',
    dateField: 'close_date',
  },
  {
    id: 'sales-by-rep',
    name: 'Sales by Rep',
    description: 'Total opportunity amount grouped by owner',
    category: 'sales',
    dataSource: 'opportunities',
    fields: [
      { name: 'owner_name', aggregation: 'none' },
      { name: 'amount', aggregation: 'sum' },
      { name: 'name', aggregation: 'count' },
    ],
    groupBy: 'owner_name',
    chartType: 'bar',
    dateField: 'close_date',
  },
  {
    id: 'won-deals-quarter',
    name: 'Won Deals This Quarter',
    description: 'Closed-won opportunities for the current quarter',
    category: 'sales',
    dataSource: 'opportunities',
    fields: [
      { name: 'name', aggregation: 'none' },
      { name: 'amount', aggregation: 'none' },
      { name: 'close_date', aggregation: 'none' },
      { name: 'account_name', aggregation: 'none' },
    ],
    chartType: 'table',
    filters: [{ field: 'stage_name', operator: 'equals', value: 'Closed Won' }],
    dateField: 'close_date',
  },
  {
    id: 'lost-deals',
    name: 'Lost Deals Analysis',
    description: 'Closed-lost opportunities by stage and amount',
    category: 'sales',
    dataSource: 'opportunities',
    fields: [
      { name: 'stage_name', aggregation: 'none' },
      { name: 'amount', aggregation: 'sum' },
      { name: 'name', aggregation: 'count' },
    ],
    groupBy: 'stage_name',
    chartType: 'pie',
    filters: [
      { field: 'is_closed', operator: 'equals', value: 'true' },
      { field: 'stage_name', operator: 'not_equals', value: 'Closed Won' },
    ],
    dateField: 'close_date',
  },
  {
    id: 'revenue-trend',
    name: 'Revenue Trend',
    description: 'Monthly revenue from closed-won deals over time',
    category: 'sales',
    dataSource: 'opportunities',
    fields: [
      { name: 'close_date', aggregation: 'none' },
      { name: 'amount', aggregation: 'sum' },
    ],
    groupBy: 'close_date',
    dateGrouping: 'month',
    chartType: 'area',
    filters: [{ field: 'stage_name', operator: 'equals', value: 'Closed Won' }],
    dateField: 'close_date',
  },
  {
    id: 'forecast',
    name: 'Forecast',
    description: 'Open opportunity amounts by expected close month',
    category: 'sales',
    dataSource: 'opportunities',
    fields: [
      { name: 'close_date', aggregation: 'none' },
      { name: 'amount', aggregation: 'sum' },
      { name: 'name', aggregation: 'count' },
    ],
    groupBy: 'close_date',
    dateGrouping: 'month',
    chartType: 'line',
    filters: [{ field: 'is_closed', operator: 'equals', value: 'false' }],
    dateField: 'close_date',
  },
  // Leads (4)
  {
    id: 'leads-by-source',
    name: 'Leads by Source',
    description: 'Count of leads grouped by lead source',
    category: 'leads',
    dataSource: 'leads',
    fields: [
      { name: 'lead_source', aggregation: 'none' },
      { name: 'id', aggregation: 'count' },
    ],
    groupBy: 'lead_source',
    chartType: 'pie',
    dateField: 'created_date',
  },
  {
    id: 'lead-conversion',
    name: 'Lead Conversion Rate',
    description: 'Converted vs not-converted leads by source',
    category: 'leads',
    dataSource: 'leads',
    fields: [
      { name: 'lead_source', aggregation: 'none' },
      { name: 'id', aggregation: 'count' },
    ],
    groupBy: 'lead_source',
    chartType: 'bar',
    filters: [{ field: 'is_converted', operator: 'equals', value: 'true' }],
    dateField: 'created_date',
  },
  {
    id: 'stale-leads',
    name: 'Stale Leads',
    description: 'Open leads with no recent activity',
    category: 'leads',
    dataSource: 'leads',
    fields: [
      { name: 'name', aggregation: 'none' },
      { name: 'company', aggregation: 'none' },
      { name: 'status', aggregation: 'none' },
      { name: 'lead_source', aggregation: 'none' },
      { name: 'created_date', aggregation: 'none' },
    ],
    chartType: 'table',
    filters: [{ field: 'is_converted', operator: 'equals', value: 'false' }],
    dateField: 'created_date',
  },
  {
    id: 'lead-aging',
    name: 'Lead Aging',
    description: 'Leads grouped by creation month showing aging',
    category: 'leads',
    dataSource: 'leads',
    fields: [
      { name: 'created_date', aggregation: 'none' },
      { name: 'id', aggregation: 'count' },
    ],
    groupBy: 'created_date',
    dateGrouping: 'month',
    chartType: 'bar',
    filters: [{ field: 'is_converted', operator: 'equals', value: 'false' }],
    dateField: 'created_date',
  },
  // Activities (3)
  {
    id: 'tasks-by-status',
    name: 'Tasks by Status',
    description: 'Count of tasks grouped by status',
    category: 'activities',
    dataSource: 'tasks',
    fields: [
      { name: 'status', aggregation: 'none' },
      { name: 'subject', aggregation: 'count' },
    ],
    groupBy: 'status',
    chartType: 'bar',
    dateField: 'activity_date',
  },
  {
    id: 'overdue-tasks',
    name: 'Overdue Tasks',
    description: 'Open tasks past their due date',
    category: 'activities',
    dataSource: 'tasks',
    fields: [
      { name: 'subject', aggregation: 'none' },
      { name: 'status', aggregation: 'none' },
      { name: 'priority', aggregation: 'none' },
      { name: 'activity_date', aggregation: 'none' },
      { name: 'owner_name', aggregation: 'none' },
    ],
    chartType: 'table',
    filters: [{ field: 'is_closed', operator: 'equals', value: 'false' }],
    dateField: 'activity_date',
  },
  {
    id: 'activity-volume',
    name: 'Activity Volume',
    description: 'Task creation trend by month',
    category: 'activities',
    dataSource: 'tasks',
    fields: [
      { name: 'created_date', aggregation: 'none' },
      { name: 'id', aggregation: 'count' },
    ],
    groupBy: 'created_date',
    dateGrouping: 'month',
    chartType: 'area',
    dateField: 'created_date',
  },
  // Support (3)
  {
    id: 'tasks-by-priority',
    name: 'Open Tasks by Priority',
    description: 'Open tasks grouped by priority level',
    category: 'support',
    dataSource: 'tasks',
    fields: [
      { name: 'priority', aggregation: 'none' },
      { name: 'subject', aggregation: 'count' },
    ],
    groupBy: 'priority',
    chartType: 'pie',
    filters: [{ field: 'is_closed', operator: 'equals', value: 'false' }],
    dateField: 'activity_date',
  },
  {
    id: 'task-completion-trend',
    name: 'Task Completion Trend',
    description: 'Completed tasks over time',
    category: 'support',
    dataSource: 'tasks',
    fields: [
      { name: 'created_date', aggregation: 'none' },
      { name: 'id', aggregation: 'count' },
    ],
    groupBy: 'created_date',
    dateGrouping: 'month',
    chartType: 'line',
    filters: [{ field: 'is_closed', operator: 'equals', value: 'true' }],
    dateField: 'created_date',
  },
  {
    id: 'tasks-by-owner',
    name: 'Tasks by Owner',
    description: 'Task distribution across team members',
    category: 'support',
    dataSource: 'tasks',
    fields: [
      { name: 'owner_name', aggregation: 'none' },
      { name: 'subject', aggregation: 'count' },
    ],
    groupBy: 'owner_name',
    chartType: 'bar',
    dateField: 'activity_date',
  },
  // Accounts (2)
  {
    id: 'top-accounts-revenue',
    name: 'Top Accounts by Revenue',
    description: 'Accounts sorted by annual revenue',
    category: 'accounts',
    dataSource: 'accounts',
    fields: [
      { name: 'name', aggregation: 'none' },
      { name: 'industry', aggregation: 'none' },
      { name: 'annual_revenue', aggregation: 'none' },
      { name: 'number_of_employees', aggregation: 'none' },
    ],
    chartType: 'table',
  },
  {
    id: 'accounts-by-industry',
    name: 'Account Distribution by Industry',
    description: 'Number of accounts in each industry',
    category: 'accounts',
    dataSource: 'accounts',
    fields: [
      { name: 'industry', aggregation: 'none' },
      { name: 'id', aggregation: 'count' },
    ],
    groupBy: 'industry',
    chartType: 'pie',
  },
  // Activity Reports
  {
    id: 'activities-created-this-month',
    name: 'Activities Created - This Month',
    description: 'Tasks and calls created this month, grouped by team member with bar chart',
    category: 'support',
    dataSource: 'tasks',
    fields: [
      { name: 'owner_name', aggregation: 'none' },
      { name: 'id', aggregation: 'count' },
    ],
    groupBy: 'owner_name',
    chartType: 'bar',
    dateField: 'created_date',
  },
]

export type ReportAction =
  | { type: 'SET_DATA_SOURCE'; dataSource: string }
  | { type: 'SET_FIELDS'; fields: ReportField[] }
  | { type: 'TOGGLE_FIELD'; fieldId: string }
  | { type: 'SET_FIELD_AGGREGATION'; fieldId: string; aggregation: AggregationType }
  | { type: 'ADD_FILTER'; filter: ReportFilter }
  | { type: 'ADD_FILTERS'; filters: ReportFilter[] }
  | { type: 'UPDATE_FILTER'; filterId: string; updates: Partial<ReportFilter> }
  | { type: 'REMOVE_FILTER'; filterId: string }
  | { type: 'SET_FILTERS'; filters: ReportFilter[] }
  | { type: 'SET_GROUP_BY'; groupBy: string | undefined }
  | { type: 'SET_DATE_GROUPING'; dateGrouping: DateGrouping | undefined }
  | { type: 'SET_SORT'; sortBy: string | undefined; sortOrder: 'asc' | 'desc' }
  | { type: 'SET_CHART_TYPE'; chartType: ChartType }
  | { type: 'SET_LIMIT'; limit: number }
  | { type: 'SET_NAME'; name: string }
  | { type: 'APPLY_TEMPLATE'; config: Partial<ReportConfig> }
  | { type: 'CLEAR_ALL' }

export function reportReducer(state: ReportConfig, action: ReportAction): ReportConfig {
  switch (action.type) {
    case 'SET_DATA_SOURCE':
      return {
        ...state,
        dataSource: action.dataSource,
        fields: [],
        filters: [],
        groupBy: undefined,
        dateGrouping: undefined,
      }
    case 'SET_FIELDS':
      return { ...state, fields: action.fields }
    case 'TOGGLE_FIELD':
      return {
        ...state,
        fields: state.fields.map((f) =>
          f.id === action.fieldId
            ? { ...f, selected: !f.selected, aggregation: f.selected ? 'none' : f.aggregation }
            : f
        ),
      }
    case 'SET_FIELD_AGGREGATION':
      return {
        ...state,
        fields: state.fields.map((f) =>
          f.id === action.fieldId ? { ...f, aggregation: action.aggregation } : f
        ),
      }
    case 'ADD_FILTER':
      return { ...state, filters: [...state.filters, action.filter] }
    case 'ADD_FILTERS':
      return { ...state, filters: [...state.filters, ...action.filters] }
    case 'UPDATE_FILTER':
      return {
        ...state,
        filters: state.filters.map((f) =>
          f.id === action.filterId ? { ...f, ...action.updates } : f
        ),
      }
    case 'REMOVE_FILTER':
      return { ...state, filters: state.filters.filter((f) => f.id !== action.filterId) }
    case 'SET_FILTERS':
      return { ...state, filters: action.filters }
    case 'SET_GROUP_BY':
      return { ...state, groupBy: action.groupBy }
    case 'SET_DATE_GROUPING':
      return { ...state, dateGrouping: action.dateGrouping }
    case 'SET_SORT':
      return { ...state, sortBy: action.sortBy, sortOrder: action.sortOrder }
    case 'SET_CHART_TYPE':
      return { ...state, chartType: action.chartType }
    case 'SET_LIMIT':
      return { ...state, limit: action.limit }
    case 'SET_NAME':
      return { ...state, name: action.name }
    case 'APPLY_TEMPLATE':
      return { ...state, ...action.config }
    case 'CLEAR_ALL':
      return {
        ...state,
        name: '',
        fields: state.fields.map((f) => ({
          ...f,
          selected: false,
          aggregation: 'none' as AggregationType,
        })),
        filters: [],
        groupBy: undefined,
        dateGrouping: undefined,
        sortBy: undefined,
        sortOrder: 'asc',
        chartType: 'table',
        limit: 10000,
      }
    default:
      return state
  }
}
